import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supportTicketService } from 'api/services/SupportTicketService';
import type { SupportTicketViewModel } from 'api/types/support-ticket';
import type { SupportTicketStatusViewModel } from 'api/types/support-ticket';
import type { SupportTicketCategoryViewModel } from 'api/types/support-ticket';
import Button from 'components/Button';

const PAGE_SIZE = 10;

export interface AdminSupportTicketTableProps {
  detailPath?: (id: string) => string;
}

const AdminSupportTicketTable: React.FC<AdminSupportTicketTableProps> = ({
  detailPath = (id) => `/admin-panel/support-tickets/${id}`,
}) => {
  const [tickets, setTickets] = useState<SupportTicketViewModel[]>([]);
  const [statuses, setStatuses] = useState<SupportTicketStatusViewModel[]>([]);
  const [categories, setCategories] = useState<SupportTicketCategoryViewModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [startingId, setStartingId] = useState<string | null>(null);
  const navigate = useNavigate();

  const loadFilters = useCallback(async () => {
    const [statusRes, categoryRes] = await Promise.all([
      supportTicketService.getStatuses(),
      supportTicketService.getCategories(),
    ]);
    if (statusRes.isSuccess && statusRes.data) setStatuses(Array.isArray(statusRes.data) ? statusRes.data : []);
    if (categoryRes.isSuccess && categoryRes.data) setCategories(Array.isArray(categoryRes.data) ? categoryRes.data : []);
  }, []);

  const loadPage = useCallback(async (page: number) => {
    setLoading(true);
    setError(null);
    try {
      const result = await supportTicketService.getAdminTickets({
        pagination: { pageNumber: page, pageSize: PAGE_SIZE },
        supportStatusId: statusFilter || undefined,
        supportCategoryId: categoryFilter || undefined,
      });
      if (result.isSuccess && result.data) {
        const list = Array.isArray(result.data) ? result.data : [];
        setTickets(list);
        setTotalPages(result.pagination?.totalPageCount ?? 1);
      } else {
        setTickets([]);
        setError(result.message ?? 'Talepler yüklenemedi.');
      }
    } catch (err) {
      setTickets([]);
      setError(err instanceof Error ? err.message : 'Bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, categoryFilter]);

  useEffect(() => {
    loadFilters();
  }, [loadFilters]);

  useEffect(() => {
    loadPage(currentPage);
  }, [currentPage, loadPage]);

  const handleStartSupport = async (ticketId: string) => {
    setStartingId(ticketId);
    try {
      const result = await supportTicketService.startSupport(ticketId);
      if (result.isSuccess && result.data?.conversationId) {
        navigate(detailPath(ticketId) + '?openChat=1');
        return;
      }
      if (result.isSuccess) {
        loadPage(currentPage);
      }
    } finally {
      setStartingId(null);
    }
  };

  const pageNumbers = Array.from({ length: Math.max(0, totalPages) }, (_, i) => i + 1);

  return (
    <div className="card">
      <div className="card-header d-flex flex-wrap align-items-center gap-2">
        <h6 className="mb-0">Destek Talepleri</h6>
        <select
          className="form-select form-select-sm w-auto"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
        >
          <option value="">Tüm durumlar</option>
          {statuses.map((s) => (
            <option key={String(s.id)} value={String(s.id)}>{s.name ?? s.id}</option>
          ))}
        </select>
        <select
          className="form-select form-select-sm w-auto"
          value={categoryFilter}
          onChange={(e) => { setCategoryFilter(e.target.value); setCurrentPage(1); }}
        >
          <option value="">Tüm kategoriler</option>
          {categories.map((c) => (
            <option key={String(c.id)} value={String(c.id)}>{c.name ?? c.id}</option>
          ))}
        </select>
      </div>
      <div className="card-body p-0">
        {error && (
          <div className="alert alert-warning rounded-0 mb-0 py-2" role="alert">
            {error}
          </div>
        )}
        {loading ? (
          <div className="d-flex align-items-center justify-content-center py-5">
            <div className="spinner-border text-primary" role="status" />
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="table table-hover mb-0 sb-table-theme">
                <thead>
                  <tr>
                    <th>Talep</th>
                    <th>Kategori</th>
                    <th>Durum</th>
                    <th>Tarih</th>
                    <th className="text-end">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.map((t) => (
                    <tr key={t.id}>
                      <td>
                        <Link to={detailPath(t.id)} className="text-decoration-none">
                          #{String(t.id).slice(0, 8)}…
                        </Link>
                      </td>
                      <td>{t.supportCategoryName ?? '-'}</td>
                      <td>{t.supportStatusName ?? '-'}</td>
                      <td>{t.createdAtUtc ? new Date(t.createdAtUtc).toLocaleDateString('tr-TR') : '-'}</td>
                      <td className="text-end">
                        <Link to={detailPath(t.id)} className="btn btn-sm btn-outline-primary me-1">
                          Detay
                        </Link>
                        {!t.conversationId && (
                          <Button
                            className="btn btn-sm btn-primary"
                            onClick={() => handleStartSupport(t.id)}
                            disabled={startingId !== null}
                          >
                            {startingId === t.id ? (
                              <span className="spinner-border spinner-border-sm" role="status" />
                            ) : (
                              'Destek Başlat'
                            )}
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {tickets.length === 0 && (
              <div className="text-muted text-center py-4">Kayıt yok.</div>
            )}
            {totalPages > 1 && (
              <nav className="p-3 border-top" aria-label="Sayfalar">
                <ul className="pagination pagination-sm mb-0 justify-content-center">
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <Button
                      className="page-link"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      Önceki
                    </Button>
                  </li>
                  {pageNumbers.map((num) => (
                    <li key={num} className={`page-item ${currentPage === num ? 'active' : ''}`}>
                      <Button className="page-link" onClick={() => setCurrentPage(num)}>
                        {num}
                      </Button>
                    </li>
                  ))}
                  <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <Button
                      className="page-link"
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Sonraki
                    </Button>
                  </li>
                </ul>
              </nav>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminSupportTicketTable;
